import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
    Form,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
} from "reactstrap";
import _ from "lodash";
import { useHistory } from "react-router";
import Cookies from "universal-cookie";
import { Link, Redirect, useLocation } from "react-router-dom";
import useAsync from "react-use-async-hook";
import { Formik } from "formik";
import * as yup from "yup";
import { useMsal } from "@azure/msal-react";

import * as AuthAPI from "../../../api/auth";
import * as DecedentAPI from "../../../api/dashboard";
import { useSetLoading } from "../../utils/hooks/loader";
import addNotification from "../../utils/notification/notification";
import clLogo from "../../../assets/images/clLogo.png";
import { doLogout, handleAuthLogout } from "../../../utils";
import { loginRequest } from "../../../authConfig";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import TooltipAndInfoForImage from "../../TooltipAndInfo/toolTipandInfoForImage";
import AdminIcon from '../../../assets/images/userWithFill.svg';

const Signin = ({
    loggedIn,
    isAdmin,
    refreshSession,
    setLoginPage,
    setIsAdmin,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [redirectURL, setRedirectURL] = useState(null);
    const [isRemember, setRemember] = useState(false);
    const [role, setRole] = useState(null);
    const [errorMsg, setErrorMsg] = useState()
    const [isDecedent, setIsDecedent] = useState();
    const [signInData, setSignInData] = useState()
    const history = useHistory();
    const location = useLocation();
    const { instance, accounts } = useMsal();
    const [showLoginTooltip, setShowLoginTooltip] = useState(false);

    const isAzureMFAEnabled = process.env.REACT_APP_IS_AZURE_MFA_ENABLED;
    useEffect(() => {
        setLoginPage(true);
        const search =
            location.search ||
            location.pathname.slice(location.pathname.indexOf("?"));
        const urlFromOnePortal = new URLSearchParams(search).get("onePortal");
        const beneficiaryUrl = new URLSearchParams(search).get("beneficiary");
        if (urlFromOnePortal) {
            setRole("cl-arranger");
            setIsAdmin(true);
        } else if (beneficiaryUrl) {
            setRole("admin")
            setIsAdmin(true)
        } else if (location?.state?.referrer.includes('/admin')) {
            setRole("admin")
            setIsAdmin(true)
        }
        else {
            setRole(isAdmin ? "admin" : "arranger");
        }
    }, [isAdmin]);

      /**
     * Get Decedent List
     */
      const {
        data: decedents,
        loading: decedentLoader,
        execute: executeGetDecedent,
    } = useAsync({
        autoExecute: false,
        task: DecedentAPI.getDecedentList,
        dataLoader: useCallback(res => {
            setIsDecedent(true)
            return _.get(res, "data.decedent");
        }, []),
        onError: useCallback(error => {
            if (error){
                setErrorMsg(error.response.data.message)
                setIsDecedent(false)
            }
        }, []),
    });

    useEffect(() => {
        let decedent;
        if(decedents){
            if (_.get(signInData, "role") === "arranger") {
                if (_.get(signInData, "isNewUser")) {
                    setRedirectURL("/profile");
                } else {
                    setRedirectURL("/app");
                }
                setLoginPage(false);
            } else if(_.get(signInData, "role") === "admin"){
                setRedirectURL("/admin");
            }
            const pathState = { ...location, state: location.state || "" };
            const {
                state: { referrer },
            } = pathState;
            //If their is referrer directly push that to history
            if (referrer) history.push(referrer);
            //On cl-arranger login session expired we are getting state undfined 
            //so by getting opi from local storage we are pushing path to history
            if (
              role === "cl-arranger" &&
              isAzureMFAEnabled &&
              !referrer &&
              localStorage.getItem("opi")
            )
              history.push(
                `/admin/${localStorage.getItem(
                  "opi"
                )}/arrangements?onePortal=true`
              );
            //If we already login as arranger or admin then we are redirecting from OP
            //there also we are getting state undefined so getting path from local storage 
            if (
              role === "cl-arranger" &&
              isAzureMFAEnabled &&
              !referrer &&
              localStorage.getItem("pathFromOp")
            ) {
              history.push(
                localStorage.getItem("pathFromOp")
              );
              localStorage.removeItem('pathFromOp')
            }
            if (
                decedents &&
                decedents.activeCases &&
                decedents.activeCases.length
            ) {
                let activeCase = decedents.activeCases;
                decedent = _.get(activeCase, "[0].decedent.onePortalId");
            } else if (
                decedents &&
                decedents.closedCases &&
                decedents.closedCases.length
            ) {
                let closedCase = decedents.closedCases;
                decedent = _.get(closedCase, "[0].decedent.onePortalId");
            }

            loggedIn(
                _.get(signInData, "role"),
                _.get(signInData, "isNewUser"),
                decedent,
            );
        } else if(isDecedent === false){
            if (_.get(signInData, "role") === "arranger") {
                if (_.get(signInData, "isNewUser")) {
                    setRedirectURL("/profile");
                } else {
                    setRedirectURL("/app");
                }
                setLoginPage(false);
            } else if(_.get(signInData, "role") === "admin"){
                setRedirectURL("/admin");
            }
            const pathState = { ...location, state: location.state || "" };
            const {
                state: { referrer },
            } = pathState;
            history.push(referrer);
            loggedIn(
                _.get(signInData, "role"),
                _.get(signInData, "isNewUser"),
                decedent,
                errorMsg
            );
        }
    },[decedents, signInData, isDecedent, errorMsg])

    /**
     * Login API
     */
    const {
        data: signInDetail,
        execute: executeSignIn,
        loading: signInLoader,
        // error: signInError
    } = useAsync({
        autoExecute: false,
        task: useCallback(async (payload, formikBag, isAdmin, role) => {
            return {
                res: await AuthAPI.doSignIn({
                    payload,
                    role: role,
                }),
                formikBag,
            };
        }, []),
        dataLoader: useCallback(({ res, formikBag }) => {
            if(res.data.data.role === "admin" || res.data.data.role === "arranger"){
                addNotification({
                  type: "success",
                  title: "Success",
                  message: "User Logged In Successfully",
              });
          }
          if(formikBag) formikBag.setSubmitting(false);
            return _.get(res, "data.data");
        }, []),
        onError: useCallback(error => {
            if (_.get(error, "response.status") === 400) {
                addNotification({
                    type: "danger",
                    title: "Error",
                    message: _.get(
                        error,
                        "response.data.message",
                        'Oops! something went wrong...'
                    ),
                });
                refreshSession();
              } 
            else {
              addNotification({
                type: "danger",
                title: "Error",
                message: _.get(
                  error,
                  "response.data.message",
                  ),
              });
              refreshSession();
            }
            if (isAzureMFAEnabled?.toLowerCase() === "true" && role === "cl-arranger") {
              setTimeout(() => {
                const logouRredirectUrl = process.env.REACT_APP_HOST !== 'https://temp.clcafamily.com' ? '/obituaries' : '/'
                handleAuthLogout(instance, accounts, logouRredirectUrl);
              }, 3000);
            }
      }, []),
    });

    const handleAuthToken = () => {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          executeSignIn(
            {
              tokenId: response.idToken
            },
            null,
            isAdmin,
            role
          );
        });
    };

    useEffect(() => {
      if (isAzureMFAEnabled?.toLowerCase() === "true" && role === "cl-arranger")
        handleAuthToken();
    }, [role]);

    useEffect(() => {
      refreshSession();
    }, []);

    useEffect(() => {
        if (signInDetail) {
          executeGetDecedent()
          setSignInData(signInDetail)
          if (isRemember) {
            if (role) {
              localStorage.setItem("role", role);
            } else {
              localStorage.setItem("role", _.get(signInDetail, "role"));
            }
          } else {
            if (role) {
              sessionStorage.setItem("role", role);
            } else {
              sessionStorage.setItem("role", _.get(signInDetail, "role"));
            }
            sessionStorage.setItem("token", _.get(signInDetail, "token"));
          }
          const cookies = new Cookies();
          cookies.set("token", _.get(signInDetail, "token"), { path: "/" });
          cookies.set("role", _.get(signInDetail, "role"), { path: "/" });
        }
        }, [signInDetail]);
        
        const showHideLinkToolTip = () => {
            setShowLoginTooltip(!setShowLoginTooltip);
        };

    const showLoader = useMemo(() => signInLoader || decedentLoader, [signInLoader, decedentLoader]);
    useSetLoading(showLoader);

    return (
    <HelmetProvider>
      <Helmet>
        <title>{"CypressLawn - Family Arrangement Assistant"}</title>
      </Helmet>
      {isAzureMFAEnabled?.toLowerCase() === "true" && role === "cl-arranger" ? (
        <div></div>
      ) : (
        <div className='signin-section'>
          <Formik
            initialValues={{
              email: "",
              password: "",
            }}
            validationSchema={yup.object({
              email: yup
                .string()
                .email("Invalid Email")
                .required("E-mail Id is required"),
              password: yup.string().required("Password is required"),
            })}
            onSubmit={(values, formikBag) => {
              executeSignIn(values, formikBag, isAdmin, role);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleSubmit,
              handleChange,
              handleBlur,
              isSubmitting,
              setSubmitting,
              setFieldValue,
            }) => {
              const onChange = e => {
                const { name, value } = e.target;
                setFieldValue(name, value);
                setSubmitting(false);
              };
              return (
                <Form id='signInForm' onSubmit={handleSubmit}>
                  {!isAdmin ? (
                    <div>
                      <h1 className='text-center' style={{ fontSize: 36 }}>
                        Family Arrangement Assistant
                      </h1>
                      <p className='text-center' style={{ fontSize: 28 }}>
                        Welcome to Your Family Portal
                      </p>
                    </div>
                  ) : (
                    <div className='admin-header-wrapper' align='center'>
                      <span className='heading text-center'>
                        Administrative Login
                      </span>
                      <br />
                      <span className='sub-heading text-center'>
                        This portal is for admin for advanced privileges.
                      </span>
                    </div>
                  )}
                  <FormGroup>
                    <Label className='form-label-theme' for='email'>
                      Email
                    </Label>
                    <Input
                      className='form-input-theme'
                      placeholder='Enter your Email Address'
                      type='text'
                      id='email'
                      name='email'
                      value={values.email}
                      onChange={onChange}
                      onBlur={handleBlur}
                    />
                    {errors.email && touched.email ? (
                      <span className='error-input-msg'>{errors.email}</span>
                    ) : null}
                  </FormGroup>
                  <FormGroup>
                    <Label className='form-label-theme' for='password'>
                      Password
                    </Label>
                    <InputGroup>
                      <Input
                        className='form-input-theme'
                        placeholder='Enter your Password'
                        type={showPassword ? "text" : "password"}
                        id='password'
                        name='password'
                        value={values.password}
                        onChange={onChange}
                        onBlur={handleBlur}
                      />
                      <InputGroupAddon addonType='append'>
                        <InputGroupText>
                          {!showPassword ? (
                            <i
                              onClick={() => setShowPassword(true)}
                              className='fas fa-eye'
                            ></i>
                          ) : (
                            <i
                              onClick={() => setShowPassword(false)}
                              className='fas fa-eye-slash'
                            ></i>
                          )}
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                    {errors.password && touched.password ? (
                      <span className='error-input-msg'>{errors.password}</span>
                    ) : null}
                  </FormGroup>
                  <FormGroup className='custom-check d-flex align-items-center justify-content-between'>
                    <Label className='checkbox-inline d-flex align-items-center justify-content-start'>
                      <Input
                        type='checkbox'
                        name='rememberMe'
                        onChange={() => setRemember(!isRemember)}
                      />
                      <span className='mr-3'>
                        <i className='fas fa-check'></i>
                      </span>
                      Remember Me
                    </Label>
                    {role === 'cl-arranger' ? (
                      <Link
                        className='forgot-password text-center d-block'
                        to="#"
                        onClick={() => {
                          addNotification({
                            type: "danger",
                            title: "No access",
                            message:
                              "CL-Arranger can change password from one-portal",
                          });
                        }}
                      >
                        Forgot password?
                      </Link>
                    ) : (
                      <Link
                        className='forgot-password text-center d-block'
                        to={{
                          pathname: '/forgot-password',
                          state: { role: role },
                        }}
                      >
                        Forgot password?
                      </Link>
                    )}
                  </FormGroup>
                  <button
                    type='submit'
                    className='text-uppercase btn-theme theme-green'
                    disabled={isSubmitting}
                  >
                    Sign In
                  </button>
                  {!isAdmin && (
                     <TooltipAndInfoForImage
                     icon={AdminIcon}
                     showTooltipFn={showHideLinkToolTip}
                     showToolTip={showLoginTooltip}
                     tooltipText={'Login as Admin'}
                     onClick={() => setIsAdmin(true)}
                     clsName={'btn-auth login-admin-extra'}
                     id='mobileAdminLoginIcon'
                 />
                  )}
                </Form>
              );
            }}
          </Formik>
          {redirectURL && <Redirect to={redirectURL} />}
        </div>
      )}
    </HelmetProvider>
  );
};

export default Signin;
